/**
 * aiCreateModel.js - the pure side of "Create from screenshot / text": what
 * POST /api/ai/extract found -> the starting values of the EXISTING create
 * forms (AccountForm, the contact fields, EditOpportunityModal, ActivityForm).
 * Nothing here saves; the forms do, with all their own rules.
 */
import { MEETING_TYPES } from '../../data/crmStages';

export const MAX_FILES = 5; // matches server/routes/aiExtract.js
export const MAX_TOTAL_MB = 10;
export const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,application/pdf';
const READABLE = ACCEPT.split(',');

/** Can the server read this file (a screenshot, photo or PDF)? */
export const readable = (file) => !!file && READABLE.includes(file.type);

/**
 * The two tabs' tools are separate (Sharif, 10/6/26):
 *   'accounts'       the Accounts tab - the account, its contacts, an activity
 *   'opportunities'  the Opportunities tab - an opportunity, on an account
 *                    that already exists (or is made first, with Accounts Edit)
 * Each shows only its own cards, and only to someone with Edit on its area.
 */
export const MODES = {
  accounts: {
    button: 'Create account from screenshot / text',
    title: 'Create account from screenshot / text',
    intro:
      'Paste an email, a signature or a business card, or add screenshots. The assistant fills in the account, its contacts and an activity; you check them before anything is created.',
    cards: { contacts: true, opportunity: false, activity: true },
  },
  opportunities: {
    button: 'Create opportunity from screenshot / text',
    title: 'Create opportunity from screenshot / text',
    intro:
      'Paste an RFQ or a customer email, or add screenshots or a PDF. The assistant fills in the opportunity and finds its account; you check it before anything is created.',
    cards: { contacts: false, opportunity: true, activity: false },
  },
};

/** Who sees what on a tab: the button at all, and each card's action. */
export function aiCreateRights(canAccounts, canOpps, mode = 'accounts') {
  if (mode === 'opportunities') {
    return {
      any: !!canOpps,
      account: !!canAccounts, // only when the account does not exist yet
      contact: false,
      activity: false,
      opportunity: !!canOpps, // POST /api/projects
    };
  }
  return {
    any: !!canAccounts,
    account: !!canAccounts, // create an account
    contact: !!canAccounts, // POST /api/accounts/:id/contacts
    activity: !!canAccounts, // POST /api/accounts/:id/activity
    opportunity: false,
  };
}

/** The extracted company -> AccountForm's prefill. */
export const accountPrefill = (a) => ({
  companyName: (a && a.name) || '',
  industry: (a && a.industry) || '',
  phone: (a && a.phone) || '',
  website: (a && a.website) || '',
  location: (a && a.location) || '',
});

/** One extracted person -> the contact fields' form (ContactFields / contactPayload). */
export const contactForm = (c, accountId = '') => ({
  accountId,
  name: (c && c.name) || '',
  role: (c && c.title) || '',
  contactRole: '',
  email: (c && c.email) || '',
  phone: (c && c.phone) || '',
  linkedinUrl: '',
  isPrimary: false,
  contactStatus: 'Active',
});

/** A company with sites takes no task or opportunity link of its own (the user picks the plant). */
export const companyHasSites = (account, allAccounts = []) =>
  !!account && !account.parentAccountId && ((account.siteCount || 0) > 0 || allAccounts.some((a) => a.parentAccountId === account.id));

/**
 * The extracted request -> a NEW opportunity seed for EditOpportunityModal,
 * linked the way the account panel's "+ New Opportunity" links it. A value the
 * customer stated is not our quoted price, so it goes into the description.
 */
export function opportunitySeed(o, account, allAccounts = []) {
  const x = o || {};
  const desc = [x.description, x.value ? `Value stated by the customer: ${x.value}` : '', x.notes].filter(Boolean).join('\n\n');
  return {
    // the extraction calls it a topic; the record has one field for it, Name
    name: x.topic || '',
    description: desc,
    customerProjectNumber: x.customerReference || '',
    dueDate: x.dueDate || '',
    customer: account ? account.name : '',
    status: 'Open',
    accountId: account && !companyHasSites(account, allAccounts) ? account.id : '',
  };
}

/** The extracted conversation -> ActivityForm's prefill. A type it does not offer becomes Other. */
export function activityPrefill(t) {
  const x = t || {};
  const type = x.type ? MEETING_TYPES.find((m) => m.toLowerCase() === String(x.type).toLowerCase()) || 'Other' : '';
  return { type, date: x.date || '', subject: x.subject || '', text: x.summary || '' };
}

/** "Cummins - Columbus (site of Cummins)" */
export const accountLabel = (c) => (c ? `${c.name}${c.parent ? ` (site of ${c.parent})` : ''}` : '');

/** Why a candidate was offered. */
export const REASON_LABEL = { name: 'same name', domain: 'same web / email domain', similar: 'similar name' };
